// src/pages/Accounting/Reconcile/utils.ts

import { BANK_LIKE_KINDS } from "./constants";
import type {
  Account,
  AccountKind,
  BalanceSummary,
  Currency,
  Reconciliation,
  SessionFilterKey,
  SessionTransaction,
  SetupFormErrors,
  SetupFormState,
} from "./types";
import type { TabFilter } from "./Session/sessionTypes";

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CAD: "$", USD: "$", EUR: "\u20ac", GBP: "\u00a3", AUD: "$", INR: "\u20b9", MXN: "$", BRL: "R$",
};

export function formatCurrency(amount: number, currency: Currency = "CAD"): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? "$";
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return amount < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
}

export function formatCurrencySigned(amount: number, currency: Currency = "CAD"): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? "$";
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (amount > 0) return `+${symbol}${formatted}`;
  if (amount < 0) return `-${symbol}${formatted}`;
  return `${symbol}${formatted}`;
}

export function currencySymbol(currency: Currency = "CAD"): string {
  return CURRENCY_SYMBOLS[currency] ?? "$";
}

export function formatDateLong(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" });
}

export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "2-digit" });
}

export function getLastReconciliation(accountId: string, reconciliations: Reconciliation[]): Reconciliation | undefined {
  const forAccount = reconciliations
    .filter((r) => r.accountId === accountId)
    .sort((a, b) => b.periodEnd.localeCompare(a.periodEnd));
  return forAccount[0];
}

export function formatAccountLabel(account: Account): string {
  if (account.last4) return `${account.name} \u00b7 ****${account.last4}`;
  return account.name;
}

export function isBankLikeAccount(kind: AccountKind): boolean {
  return BANK_LIKE_KINDS.has(kind);
}

export function parseUserDate(input: string): string | null {
  const trimmed = input.trim();
  const parts = trimmed.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return null;
  const d = Number(dd); const m = Number(mm); const y = Number(yyyy);
  if (!Number.isInteger(d) || !Number.isInteger(m) || !Number.isInteger(y)) return null;
  if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100) return null;
  const iso = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const parsed = new Date(iso);
  if (isNaN(parsed.getTime())) return null;
  return iso;
}

export function formatIsoToUserDate(iso: string): string {
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
}

export function parseUserMoney(input: string): number | null {
  const cleaned = input.trim().replace(/[$,\s\u00a3\u20ac\u20b9]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function validateSetupForm(state: SetupFormState, beginningBalanceDate: string | null, isBankLike: boolean): SetupFormErrors {
  const errors: SetupFormErrors = {};
  const dateIso = parseUserDate(state.statementEndingDate);
  const dateLabel = isBankLike ? "Statement ending date" : "Ending date";
  const balanceLabel = isBankLike ? "Statement ending balance" : "Ending balance";
  if (!dateIso) {
    errors.statementEndingDate = `${dateLabel} is required. Enter a date in dd/mm/yyyy format.`;
  } else {
    const today = new Date().toISOString().slice(0, 10);
    if (dateIso > today) {
      errors.statementEndingDate = "Date cannot be in the future.";
    } else if (beginningBalanceDate && dateIso <= beginningBalanceDate) {
      errors.statementEndingDate = `Date must be after ${formatDateLong(beginningBalanceDate)}.`;
    }
  }
  const money = parseUserMoney(state.statementEndingBalance);
  if (money === null) {
    errors.statementEndingBalance = `${balanceLabel} is required.`;
  }
  return errors;
}

export function computeBalanceSummary(transactions: SessionTransaction[], clearedIds: Set<string>, beginningBalance: number, statementEndingBalance: number): BalanceSummary {
  const clearedTotal = transactions.reduce((sum, tx) => (clearedIds.has(tx.id) ? sum + tx.amount : sum), 0);
  const novalaCleared = beginningBalance + clearedTotal;
  const difference = Number((statementEndingBalance - novalaCleared).toFixed(2));
  return { statementEndingBalance, novalaCleared: Number(novalaCleared.toFixed(2)), difference, isBalanced: Math.abs(difference) < 0.01 };
}

export function filterSessionTransactions(transactions: SessionTransaction[], filter: SessionFilterKey, clearedIds: Set<string>): SessionTransaction[] {
  switch (filter) {
    case "all":
      return transactions;
    case "cleared":
      return transactions.filter((tx) => clearedIds.has(tx.id));
    case "needs-review":
      return transactions.filter((tx) => tx.matchStatus === "needs-review");
    case "not-in-novala":
      return transactions.filter((tx) => tx.matchStatus === "unmatched");
    default:
      return transactions;
  }
}

export function countByFilter(transactions: SessionTransaction[], clearedIds: Set<string>): Record<SessionFilterKey, number> {
  return {
    all: transactions.length,
    cleared: transactions.filter((tx) => clearedIds.has(tx.id)).length,
    "needs-review": transactions.filter((tx) => tx.matchStatus === "needs-review").length,
    "not-in-novala": transactions.filter((tx) => tx.matchStatus === "unmatched").length,
  };
}

export function filterSessionByType<T extends { amount: number }>(txs: T[], tab: TabFilter): T[] {
  if (tab === "payments") return txs.filter((t) => t.amount < 0);
  if (tab === "deposits") return txs.filter((t) => t.amount > 0);
  return txs;
}

export function countByTabFilter<T extends { amount: number }>(txs: T[]): Record<TabFilter, number> {
  return {
    all: txs.length,
    payments: txs.filter((t) => t.amount < 0).length,
    deposits: txs.filter((t) => t.amount > 0).length,
  };
}